import React, { useState, useEffect } from 'react';
import { TextField } from '@mui/material';

/**
 * Custom phone input component that formats phone number as user types
 * without using deprecated findDOMNode method
 * Compatible with react-hook-form
 */
const PhoneInput = React.forwardRef(({ onChange, onBlur, name, value, error, helperText, ...props }, ref) => {
  // Use internal state to track the formatted value
  const [displayValue, setDisplayValue] = useState('');
  
  // Format a raw phone number string to +7 (XXX) XXX-XX-XX format
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    
    // If already formatted correctly, return as is
    if (/^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format the phone number
    let formatted = '';
    if (digits.length > 0) {
      formatted = '+7';
    }
    if (digits.length > 1) {
      formatted += ` (${digits.substring(1, Math.min(4, digits.length))}`;
    }
    if (digits.length > 4) {
      formatted += `) ${digits.substring(4, Math.min(7, digits.length))}`;
    }
    if (digits.length > 7) {
      formatted += `-${digits.substring(7, Math.min(9, digits.length))}`;
    }
    if (digits.length > 9) {
      formatted += `-${digits.substring(9, Math.min(11, digits.length))}`;
    }
    
    return formatted;
  };
  
  // Handle input changes
  const handleChange = (e) => {
    const rawValue = e.target.value;
    const formattedValue = formatPhoneNumber(rawValue);
    
    // Update the displayed value
    setDisplayValue(formattedValue);
    
    // Create a synthetic event to pass to the original onChange handler
    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name,
          value: formattedValue
        }
      };
      onChange(syntheticEvent);
    }
  };
  
  // Handle blur event
  const handleBlur = (e) => {
    if (onBlur) {
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          name,
          value: displayValue
        }
      };
      onBlur(syntheticEvent);
    }
  };
  
  // Initialize display value from external value if provided
  useEffect(() => {
    if (value && typeof value === 'string') {
      const formatted = formatPhoneNumber(value);
      setDisplayValue(formatted);
    }
  }, [value]);
  
  return (
    <TextField
      {...props}
      name={name}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      inputRef={ref}
      placeholder="+7 (___) ___-__-__"
      error={error}
      helperText={helperText}
    />
  );
});

PhoneInput.displayName = 'PhoneInput';

export default PhoneInput;
